/**
 * SEP 5.b.2 — Race-guarded quote loader (PURE).
 *
 * The purchase flow fires a `saleApi.quote` request every time the
 * committed selection changes. Requests can complete out of order. The
 * QuoteLoader increments a monotonic `gen` on every `load()` call and
 * reports back an `isCurrent` flag so the caller can DISCARD stale
 * outcomes (§V.32: "old quote A must not overwrite fresh quote B").
 *
 * The loader is deliberately shape-agnostic: it takes a `fetcher`
 * function so tests can inject a controllable stub without needing an
 * HTTP mock.
 */
import type { GridSelection } from "./grid-selection";
import type { SaleQuote } from "./sale-types";

export type QuoteFetcher = (sel: GridSelection) => Promise<SaleQuote>;

export type QuoteOutcomeResult =
  | { kind: "success"; quote: SaleQuote }
  | { kind: "error"; error: unknown };

export interface QuoteOutcome {
  gen: number;
  selection: GridSelection;
  result: QuoteOutcomeResult;
  /** True iff no newer `load()` was started after this one. */
  isCurrent: boolean;
}

export class QuoteLoader {
  private gen = 0;

  constructor(private readonly fetcher: QuoteFetcher) {}

  /** Current monotonic generation counter. */
  get currentGen(): number {
    return this.gen;
  }

  /** True iff `gen` is NOT the most-recent one. */
  isStale(gen: number): boolean {
    return gen !== this.gen;
  }

  /**
   * Kick off a new load. The returned promise resolves with the
   * outcome of THIS specific load — `isCurrent` reflects the state of
   * the loader at the moment of resolution.
   */
  async load(sel: GridSelection): Promise<QuoteOutcome> {
    this.gen += 1;
    const myGen = this.gen;
    let result: QuoteOutcomeResult;
    try {
      const quote = await this.fetcher(sel);
      result = { kind: "success", quote };
    } catch (error) {
      result = { kind: "error", error };
    }
    return {
      gen: myGen,
      selection: sel,
      result,
      isCurrent: myGen === this.gen,
    };
  }
}
